// Display modes: Fullscreen (standard Fullscreen API with webkit fallback) and the
// red/cyan Anaglyph 3D toggle (THREE.AnaglyphEffect, already created in Game.InitResources).
// Rendering/UI only: no changes to cue controls, physics, lighting or shadows.
import Game from "./Game";
import {InputModeState} from "./inputModeState";

type FullscreenCapableElement = HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
};

type FullscreenCapableDocument = Document & {
    webkitFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => Promise<void> | void;
};

const doc = document as FullscreenCapableDocument;

export function getFullscreenElement(): Element | null {
    return doc.fullscreenElement || doc.webkitFullscreenElement || null;
}

export function isFullscreen(): boolean {
    return getFullscreenElement() !== null;
}

function fullscreenTarget(): FullscreenCapableElement | null {
    // The game container: its size drives the renderer size every frame (Game.Render).
    return (document.getElementById("game-placeholder") as FullscreenCapableElement | null)
        || (Game.Resources.renderer && Game.Resources.renderer.domElement) || null;
}

function ignoreRejection(result: Promise<void> | void) {
    if (result && typeof (result as Promise<void>).catch === "function") {
        (result as Promise<void>).catch((err) => console.warn("Fullscreen request failed:", err));
    }
}

/** Touch mode: play sideways. Only allowed in fullscreen, and not everywhere (iOS Safari has no lock): best effort. */
function preferLandscape() {
    if (!InputModeState.isTouch()) return;
    try {
        const o: any = (window.screen as any) && (window.screen as any).orientation;
        if (o && typeof o.lock === "function") {
            const r = o.lock("landscape");
            if (r && typeof r.catch === "function") r.catch(() => {});
        }
    } catch {}
}

export function enterFullscreen() {
    const target = fullscreenTarget();
    if (!target) return;
    let result: Promise<void> | void = undefined;
    if (target.requestFullscreen) {
        result = target.requestFullscreen();
    } else if (target.webkitRequestFullscreen) {
        result = target.webkitRequestFullscreen();
    }
    if (result && typeof (result as Promise<void>).then === "function") (result as Promise<void>).then(preferLandscape, () => {});
    ignoreRejection(result);
}

export function exitFullscreen() {
    if (doc.exitFullscreen) {
        ignoreRejection(doc.exitFullscreen());
    } else if (doc.webkitExitFullscreen) {
        ignoreRejection(doc.webkitExitFullscreen());
    }
}

export function toggleFullscreen() {
    if (isFullscreen()) {
        exitFullscreen();
    } else {
        enterFullscreen();
    }
}

// Re-size the renderer, camera aspect and anaglyph targets as soon as fullscreen changes
// (Game.Render also re-checks the container size every frame).
function resizeViewport() {
    const placeholder = Game.Resources.placeholder as HTMLElement | undefined;
    const renderer = Game.Resources.renderer;
    if (!placeholder || !placeholder.getBoundingClientRect || !renderer || !renderer.setSize) return;
    const rect = placeholder.getBoundingClientRect();
    Game.Resources.width = rect.width;
    Game.Resources.height = rect.height;
    renderer.setSize(rect.width, rect.height);
    Game.SyncViewport();
}

export function onFullscreenChange(listener: () => void): () => void {
    const handler = () => {
        resizeViewport();
        listener();
    };
    document.addEventListener("fullscreenchange", handler);
    document.addEventListener("webkitfullscreenchange", handler);
    return () => {
        document.removeEventListener("fullscreenchange", handler);
        document.removeEventListener("webkitfullscreenchange", handler);
    };
}

export function isAnaglyphEnabled(): boolean {
    return !!Game.Config.Anaglyph;
}

export function toggleAnaglyph(): boolean {
    Game.Config.Anaglyph = !Game.Config.Anaglyph;
    // Size the effect's render targets to the current canvas immediately.
    if (Game.Camera && Game.Camera.updateProjectionMatrix) {
        Game.SyncViewport();
    }
    return Game.Config.Anaglyph;
}
